import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

interface ICommitDayGroup {
  key: string;
  label: string;
  items: interfaces.IPublicCommit[];
}

/**
 * Commits list view: the repo `Commits` tab. Groups the loaded commit log by day and
 * links each row to the commit detail (`/repo/<org>/<repo>/commits/<sha>`). Mirrors the
 * Claude Design `changes.jsx` CommitsList, wired to the real public commit API. Emits
 * `dotglobal-navigate` and `dotglobal-toast`.
 */
@customElement('dotglobal-commits')
export class DotglobalCommits extends DeesElement {
  @property({ type: Object })
  public accessor data: interfaces.IReq_PublicCommits['response'] | null = null;

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .empty { padding: 48px 20px; text-align: center; color: var(--text-muted); }

      .commit-toolbar {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
        margin-bottom: 16px;
      }
      .branchpill {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 30px;
        padding: 0 12px;
        border: 1px solid var(--border-strong);
        border-radius: 6px;
        background: var(--bg-tertiary);
        color: var(--text-primary);
        font-size: 12.5px;
        font-weight: 500;
      }
      .branchpill .mono { font-family: var(--font-mono); }
      .commit-count { color: var(--text-muted); font-size: 13px; display: inline-flex; align-items: center; gap: 7px; }
      .commit-count b { color: var(--text-primary); }

      .commitgroup + .commitgroup { margin-top: 18px; }
      .commitgroup-h {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        color: var(--text-secondary);
        font-size: 12.5px;
        font-weight: 600;
      }
      .commitlist {
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        overflow: hidden;
      }
      .commitrow {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--border-subtle);
      }
      .commitrow:last-child { border-bottom: 0; }
      .commitrow:hover { background: var(--row-hover); }

      .avatar { width: 30px; height: 30px; font-size: 11px; cursor: pointer; }
      .cr-main { min-width: 0; flex: 1; }
      .cr-msg {
        display: block;
        max-width: 100%;
        text-align: left;
        color: var(--text-primary);
        font-size: 14px;
        font-weight: 600;
        line-height: 1.35;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: pointer;
      }
      .cr-msg:hover { color: var(--accent); }
      .cr-sub { margin-top: 3px; color: var(--text-muted); font-size: 12px; }
      .cr-sub b { color: var(--text-secondary); cursor: pointer; }
      .cr-sub b:hover { color: var(--accent); }

      .cr-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
      .cr-stat { font-family: var(--font-mono); font-size: 12px; display: inline-flex; gap: 7px; }
      .ad { color: var(--success); }
      .dl-c { color: var(--error); }
      .vbadge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 10.5px;
        font-weight: 600;
        color: var(--success);
      }
      .cr-sha {
        font-family: var(--font-mono);
        font-size: 12px;
        color: var(--accent);
        cursor: pointer;
        padding: 2px 6px;
        border-radius: 5px;
      }
      .cr-sha:hover { background: color-mix(in srgb, var(--accent) 12%, transparent); }
      .iconbtn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        color: var(--text-secondary);
      }
      .iconbtn:hover { background: var(--hover); color: var(--text-primary); }

      @media (max-width: 720px) {
        .cr-stat, .vbadge { display: none; }
      }
    `,
  ];

  public render(): TemplateResult {
    const data = this.data;
    const repo = data?.repo;
    if (!data || !repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }
    const commits = data.commits ?? [];
    if (!commits.length) {
      return html`<div class="page"><div class="empty">No commits were returned for this repository.</div></div>`;
    }
    const groups = this.groupByDay(commits);
    return html`
      <div class="page">
        <div class="commit-toolbar">
          <span class="branchpill">${this.icon('GitBranch', 14)} <span class="mono">${repo.defaultBranch || 'main'}</span></span>
          <span class="commit-count">${this.icon('History', 15)} Latest <b>${commits.length}</b> commit${commits.length === 1 ? '' : 's'}</span>
        </div>
        ${groups.map((group) => html`
          <div class="commitgroup">
            <div class="commitgroup-h">${this.icon('GitCommitHorizontal', 14)} Commits on ${group.label}</div>
            <div class="commitlist">
              ${group.items.map((commit) => this.renderRow(repo, commit))}
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private renderRow(repoArg: interfaces.IPublicRepo, commitArg: interfaces.IPublicCommit): TemplateResult {
    const detailHash = this.commitHash(repoArg, commitArg.sha);
    const hasStats = commitArg.additions > 0 || commitArg.deletions > 0;
    return html`
      <div class="commitrow">
        <span class="avatar" @click=${() => this.openAuthor(commitArg)}>
          ${this.initials(commitArg.authorName || commitArg.authorLogin)}
          ${commitArg.authorAvatarUrl
            ? html`<img src=${commitArg.authorAvatarUrl} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
            : ''}
        </span>
        <div class="cr-main">
          <button class="cr-msg" title=${commitArg.title} @click=${() => this.navigate(detailHash)}>${commitArg.title || commitArg.shortSha}</button>
          <div class="cr-sub">
            <b @click=${() => this.openAuthor(commitArg)}>${commitArg.authorLogin || commitArg.authorName || 'unknown'}</b>
            committed ${this.timeAgo(commitArg.createdAt)}
          </div>
        </div>
        <div class="cr-right">
          ${hasStats ? html`<span class="cr-stat"><span class="ad">+${commitArg.additions}</span><span class="dl-c">&minus;${commitArg.deletions}</span></span>` : ''}
          ${commitArg.verified ? html`<span class="vbadge" title="Verified signature">${this.icon('ShieldCheck', 11)} Verified</span>` : ''}
          <button class="cr-sha" @click=${() => this.navigate(detailHash)}>${commitArg.shortSha}</button>
          <button class="iconbtn" title="Copy SHA" @click=${() => this.copySha(commitArg.sha)}>${this.icon('Copy', 14)}</button>
        </div>
      </div>
    `;
  }

  private groupByDay(commitsArg: interfaces.IPublicCommit[]): ICommitDayGroup[] {
    const groups: ICommitDayGroup[] = [];
    for (const commit of commitsArg) {
      const key = commit.createdAt ? commit.createdAt.slice(0, 10) : 'unknown';
      let group = groups.find((candidate) => candidate.key === key);
      if (!group) {
        group = { key, label: this.dayLabel(commit.createdAt), items: [] };
        groups.push(group);
      }
      group.items.push(commit);
    }
    return groups;
  }

  private dayLabel(timestampArg: string): string {
    if (!timestampArg) {
      return 'an unknown date';
    }
    const date = new Date(timestampArg);
    if (Number.isNaN(date.getTime())) {
      return 'an unknown date';
    }
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  private commitHash(repoArg: interfaces.IPublicRepo, shaArg: string): string {
    return `#/repo/${encodeURIComponent(repoArg.org)}/${encodeURIComponent(repoArg.name)}/commits/${encodeURIComponent(shaArg)}`;
  }

  private openAuthor(commitArg: interfaces.IPublicCommit): void {
    if (commitArg.authorLogin) {
      this.navigate(`#/user/${encodeURIComponent(commitArg.authorLogin)}`);
    }
  }

  private async copySha(shaArg: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(shaArg);
      this.toast('Commit SHA copied');
    } catch {
      this.toast('Could not copy SHA');
    }
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private toast(messageArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-toast', { detail: { message: messageArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string, sizeArg = 15): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${sizeArg}></dees-icon>`;
  }

  private initials(labelArg: string): string {
    return (labelArg || 'fg').split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((partArg) => partArg[0]?.toUpperCase()).join('') || 'fg';
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    return `${Math.round(days / 30)}mo ago`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-commits': DotglobalCommits;
  }
}
